import {
  Badge,
  EmptyState,
  Menu,
  type MenuItem,
  Skeleton,
  Tabs,
  useConfirm,
  useToast,
} from '@/components';
import { useAuth } from '@/features/auth/AuthProvider';
import { formatDateTime } from '@/lib/datetime';
import { A, useNavigate } from '@solidjs/router';
import { For, Show, createMemo, createSignal } from 'solid-js';
import type { PollListItem } from './api';
import { useClosePoll, useDeletePoll, usePolls } from './hooks';

const STATUS_TONE = { draft: 'neutral', open: 'success', closed: 'neutral' } as const;

export const PollsList = () => {
  const query = usePolls();
  const [tab, setTab] = createSignal('open');
  const rows = createMemo(() => {
    const all = query.data?.data ?? [];
    return all.filter((poll) =>
      tab() === 'closed' ? poll.status === 'closed' : poll.status !== 'closed',
    );
  });
  const counts = createMemo(() => {
    const all = query.data?.data ?? [];
    return {
      open: all.filter((poll) => poll.status !== 'closed').length,
      closed: all.filter((poll) => poll.status === 'closed').length,
    };
  });

  return (
    <>
      <Tabs
        tabs={[
          { id: 'open', label: 'Open', count: counts().open },
          { id: 'closed', label: 'Closed', count: counts().closed },
        ]}
        active={tab()}
        onChange={setTab}
        class="mb-5"
      />
      <Show when={!query.isPending} fallback={<Skeleton class="h-48" />}>
        <Show
          when={!query.isError}
          fallback={<EmptyState variant="error" title="Failed to load polls" />}
        >
          <Show
            when={rows().length > 0}
            fallback={
              <EmptyState
                title={tab() === 'closed' ? 'No closed polls' : 'No open polls'}
                body="Create a survey to collect responses from the workspace."
              />
            }
          >
            <div class="grid gap-3 md:grid-cols-2">
              <For each={rows()}>
                {(poll) => (
                  <article class="rounded-xl border border-neutral-200 bg-surface p-5 dark:border-neutral-800 dark:bg-surface-dark">
                    <div class="flex items-start justify-between gap-3">
                      <div class="min-w-0">
                        <div class="mb-2 flex flex-wrap items-center gap-2">
                          <Badge tone={STATUS_TONE[poll.status]}>{poll.status}</Badge>
                          <Show when={poll.hasResponded}>
                            <Badge tone="info">you responded</Badge>
                          </Show>
                          <Show when={poll.isAnonymous}>
                            <Badge>anonymous</Badge>
                          </Show>
                        </div>
                        <A
                          href={`/polls/${poll.id}`}
                          class="font-semibold text-neutral-900 hover:text-brand-600 dark:text-neutral-100 dark:hover:text-brand-400"
                        >
                          {poll.title}
                        </A>
                        <Show when={poll.description}>
                          <p class="mt-1 line-clamp-2 text-sm text-neutral-500">
                            {poll.description}
                          </p>
                        </Show>
                      </div>
                      <PollMenu poll={poll} />
                    </div>
                    <div class="mt-4 flex flex-wrap items-center justify-between gap-2 text-xs text-neutral-500">
                      <span>
                        {poll.responseCount} response{poll.responseCount === 1 ? '' : 's'}
                      </span>
                      <span>
                        {poll.closesAt ? `Closes ${formatDateTime(poll.closesAt)}` : 'No deadline'}
                      </span>
                    </div>
                  </article>
                )}
              </For>
            </div>
          </Show>
        </Show>
      </Show>
    </>
  );
};

const PollMenu = (props: { poll: PollListItem }) => {
  const { session } = useAuth();
  const navigate = useNavigate();
  const close = useClosePoll(props.poll.id);
  const remove = useDeletePoll(props.poll.id);
  const confirm = useConfirm();
  const toast = useToast();
  const items = (): MenuItem[] => {
    const result: MenuItem[] = [
      { label: 'Open', onSelect: () => navigate(`/polls/${props.poll.id}`) },
    ];
    if (props.poll.canViewResults) {
      result.push({
        label: 'Results',
        onSelect: () => navigate(`/polls/${props.poll.id}/results`),
      });
    }
    const canDeleteAny = session()?.permissions.has('polls:delete') ?? false;
    const canClose =
      (session()?.permissions.has('polls:close') ?? false) &&
      (props.poll.canManage || canDeleteAny);
    if (props.poll.status === 'open' && canClose) {
      result.push({
        label: 'Close now',
        onSelect: async () => {
          if (!(await confirm({ title: `Close ${props.poll.title}?`, confirmLabel: 'Close' })))
            return;
          await close.mutateAsync(undefined as never);
          toast.success('Poll closed');
        },
      });
    }
    if (props.poll.canManage || canDeleteAny) {
      result.push({
        label: 'Delete',
        danger: true,
        onSelect: async () => {
          if (
            !(await confirm({
              title: `Delete ${props.poll.title}?`,
              body: 'This permanently deletes every response.',
              confirmLabel: 'Delete',
              danger: true,
            }))
          )
            return;
          await remove.mutateAsync(undefined as never);
          toast.success('Poll deleted');
        },
      });
    }
    return result;
  };
  return <Menu items={items()} />;
};
